import { useEffect, useState } from "react";

import { t } from "../../../i18n";
import { formatRelative } from "../../../lib/format";
import { trpc } from "../../../lib/trpc";
import {
  Avatar,
  Badge,
  Button,
  Card,
  DeletingOverlay,
  Dialog,
  Icon,
  IconButton,
  useToast,
} from "../../ui";
import { InviteMemberDialog } from "../dialogs/InviteMemberDialog";

interface MembersTabProps {
  householdId: string;
  members: Array<{
    userId: string;
    email: string;
    displayName: string | null;
    role: string;
    joinedAt: string;
  }>;
  pendingInvitations: Array<{
    id: string;
    email: string;
    createdAt: string;
    invitedByDisplayName: string | null;
  }>;
  revokedNotifications: Array<{
    id: string;
    email: string;
    revokedAt: string;
    reason: "invitee_deleted_account";
  }>;
  role: string;
}

export function MembersTab({
  householdId,
  members,
  pendingInvitations,
  revokedNotifications,
  role,
}: MembersTabProps) {
  const utils = trpc.useUtils();
  const { show } = useToast();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    name: string;
  } | null>(null);
  const isOwner = role === "owner";

  // One-time toast per (invitation, browser) when an invitation was
  // revoked because the invitee deleted their account.
  useEffect(() => {
    if (!isOwner || revokedNotifications.length === 0) return;
    const storageKey = "fellesen.seenRevokedInvitations";
    let seen: Set<string>;
    try {
      const raw = localStorage.getItem(storageKey);
      seen = raw ? new Set(JSON.parse(raw)) : new Set();
    } catch {
      seen = new Set();
    }
    const fresh = revokedNotifications.filter((n) => !seen.has(n.id));
    if (fresh.length === 0) return;
    for (const n of fresh) {
      show({
        id: `revoked-invitation-${n.id}`,
        tone: "info",
        title: t.household.members.revokedNotifications.title,
        description: t.household.members.revokedNotifications.description(n.email),
        dismissLabel: t.common.close,
      });
      seen.add(n.id);
    }
    try {
      localStorage.setItem(storageKey, JSON.stringify([...seen]));
    } catch {
      // localStorage full / disabled — toast still showed, just no dedupe.
    }
  }, [isOwner, revokedNotifications, show]);

  const [pendingMemberRemovals, setPendingMemberRemovals] = useState<Set<string>>(() => new Set());
  const [pendingInvitationCancels, setPendingInvitationCancels] = useState<Set<string>>(
    () => new Set(),
  );

  const removeMember = trpc.household.removeMember.useMutation({
    onMutate: ({ userId }) => {
      setPendingMemberRemovals((prev) => {
        const next = new Set(prev);
        next.add(userId);
        return next;
      });
    },
    onSuccess: async (_, { userId }) => {
      await utils.household.get.invalidate({ groupId: householdId });
      setPendingMemberRemovals((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    },
    onError: (err, { userId }) => {
      setPendingMemberRemovals((prev) => {
        if (!prev.has(userId)) return prev;
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
      show({
        tone: "danger",
        title: t.household.members.removeFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
  });

  const cancelInvitation = trpc.household.cancelInvitation.useMutation({
    onMutate: ({ invitationId }) => {
      setPendingInvitationCancels((prev) => {
        const next = new Set(prev);
        next.add(invitationId);
        return next;
      });
    },
    onSuccess: async (_, { invitationId }) => {
      await utils.household.get.invalidate({ groupId: householdId });
      setPendingInvitationCancels((prev) => {
        if (!prev.has(invitationId)) return prev;
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
    },
    onError: (err, { invitationId }) => {
      setPendingInvitationCancels((prev) => {
        if (!prev.has(invitationId)) return prev;
        const next = new Set(prev);
        next.delete(invitationId);
        return next;
      });
      show({
        tone: "danger",
        title: t.household.members.pendingInvitations.cancelFailedToast,
        description: err.message,
        dismissLabel: t.common.close,
      });
    },
  });

  return (
    <>
      {isOwner && pendingInvitations.length > 0 && (
        <div className="mb-6">
          <h2 className="mb-3 font-medium text-ink-700 text-sm">
            {t.household.members.pendingInvitations.heading(pendingInvitations.length)}
          </h2>
          <Card padding="none">
            <ul className="divide-y divide-ink-100">
              {pendingInvitations.map((inv) => (
                <li
                  key={inv.id}
                  className="relative flex items-center justify-between gap-3 p-3 sm:p-4"
                >
                  {pendingInvitationCancels.has(inv.id) && (
                    <DeletingOverlay
                      label={t.household.members.pendingInvitations.cancellingOverlay}
                    />
                  )}
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-ink-100 text-ink-500">
                      <Icon name="mail" size="sm" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-ink-900 text-sm">{inv.email}</div>
                      <div className="truncate text-ink-500 text-xs">
                        {t.household.members.pendingInvitations.invitedAt(
                          formatRelative(inv.createdAt),
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <Badge tone="warning">
                      {t.household.members.pendingInvitations.statusBadge}
                    </Badge>
                    <Button
                      onClick={() =>
                        cancelInvitation.mutate({ groupId: householdId, invitationId: inv.id })
                      }
                      disabled={pendingInvitationCancels.has(inv.id)}
                    >
                      {t.household.members.pendingInvitations.cancel}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      )}

      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-medium text-ink-700 text-sm">
          {t.household.members.countHeading(members.length)}
        </h2>
        {isOwner && (
          <Button variant="primary" iconLeft="user-plus" onClick={() => setInviteOpen(true)}>
            {t.household.members.invite}
          </Button>
        )}
      </div>

      <Card padding="none">
        <ul className="divide-y divide-ink-100">
          {members.map((m) => (
            <li key={m.userId} className="relative flex items-center justify-between p-3 sm:p-4">
              {pendingMemberRemovals.has(m.userId) && (
                <DeletingOverlay label={t.household.members.removingOverlay} />
              )}
              <div className="flex min-w-0 items-center gap-3">
                <Avatar name={m.displayName ?? m.email} />
                <div className="min-w-0">
                  <div className="truncate font-medium text-ink-900 text-sm">
                    {m.displayName ?? m.email}
                  </div>
                  <div className="truncate text-ink-500 text-xs">{m.email}</div>
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {m.role === "owner" && (
                  <Badge tone="accent" variant="solid" iconLeft="crown">
                    {t.roles.owner}
                  </Badge>
                )}
                {isOwner && m.role !== "owner" && (
                  <IconButton
                    icon="trash"
                    label={t.household.members.removeFromHousehold}
                    className="text-rose-600 hover:bg-rose-50 hover:text-rose-700 dark:text-rose-400 dark:hover:bg-rose-900/30 dark:hover:text-rose-300"
                    onClick={() =>
                      setRemoveTarget({ userId: m.userId, name: m.displayName ?? m.email })
                    }
                  />
                )}
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <InviteMemberDialog
        householdId={householdId}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
      />

      <Dialog
        open={removeTarget !== null}
        onClose={() => setRemoveTarget(null)}
        title={t.household.members.removeDialog.title}
        description={
          removeTarget ? t.household.members.removeDialog.description(removeTarget.name) : undefined
        }
        footer={
          <>
            <Button onClick={() => setRemoveTarget(null)}>{t.common.cancel}</Button>
            <Button
              variant="danger"
              onClick={() => {
                if (!removeTarget) return;
                removeMember.mutate({ groupId: householdId, userId: removeTarget.userId });
                setRemoveTarget(null);
              }}
            >
              {t.household.members.removeDialog.submit}
            </Button>
          </>
        }
      />
    </>
  );
}
